// @ts-check
import { useState } from 'react';
import { MenuItem, Menu } from '@material-ui/core';
import _ from 'lodash';
import Text from 'components/base/Text';
import { Flex, Margin } from 'components/base/layout-components';
import Icon from 'components/base/Icon';
import { withStyles } from '@material-ui/styles';
import { GRAPH_TO_ICON, DEFAULT_GRAPHS } from './consts';
import { LBButton } from '@linearb/buzz';

const MenuItemWithoutHover = withStyles({
  root: {
    'line-height': 1,
    cursor: 'inherit',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  }
})(MenuItem);

const MenuItemWithoutPadding = withStyles({
  root: {
    'line-height': 1,
    cursor: 'inherit',
    '&:hover': {
      backgroundColor: 'transparent'
    },
    padding: '0'
  }
})(MenuItemWithoutHover);

const GraphPicker = ({
  selectedGraph: _selectedGraph,
  graphs = DEFAULT_GRAPHS,
  title,
  chunks = 3,
  container,
  openOnTop = false,
  icon,
  onChange = _.noop,
  closeOnMouseLeave = false
}) => {
  const [selectedGraph, setSelectedGraph] = useState(_selectedGraph);
  const [anchorEl, setAnchorEl] = useState(null);

  if (!DEFAULT_GRAPHS.includes(selectedGraph)) return false;

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectGraph = graph => {
    setSelectedGraph(graph);
    onChange(graph);
    handleClose();
  };

  return (
    <>
      {container(
        icon ?? (
          <Icon size='medium' name={GRAPH_TO_ICON[selectedGraph]} alpha={0.7} />
        ),
        handleClick
      )}
      <Menu
        id='simple-menu'
        anchorEl={anchorEl}
        keepMounted
        autoFocus={!openOnTop}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        {...(openOnTop
          ? {
              getContentAnchorEl: null,
              anchorOrigin: { vertical: 'top', horizontal: 'center' },
              transformOrigin: { vertical: 'bottom', horizontal: 'center' }
            }
          : {})}
      >
        {title && (
          <MenuItemWithoutHover disableRipple>
            <Text color='shuttleGray' size='large'>
              {title}
            </Text>
          </MenuItemWithoutHover>
        )}
        <MenuItemWithoutHover
          disableRipple
          {...(closeOnMouseLeave ? { onMouseLeave: handleClose } : {})}
        >
          {_.chunk(graphs, chunks).map((graphChunk, _idx) => (
            <Flex direction='column' key={_idx}>
              {graphChunk.map((graphType, idx) => {
                return (
                  <Margin margin='tiny' key={idx}>
                    <LBButton
                      onClick={() => selectGraph(graphType)}
                      size='icon'
                      variant='ghost'
                    >
                      <Icon
                        data-testid={`graph-box-${idx}-${_idx}`}
                        size='medium'
                        name={GRAPH_TO_ICON[graphType]}
                        alpha={0.7}
                        {...(graphType !== selectedGraph
                          ? { color: 'botticelli' }
                          : {})}
                      />
                    </LBButton>
                  </Margin>
                );
              })}
            </Flex>
          ))}
        </MenuItemWithoutHover>
      </Menu>
    </>
  );
};

export const GraphPickerWithoutMenu = ({
  selectedGraph,
  graphs = DEFAULT_GRAPHS,
  chunks = 3,
  onChange = _.noop
}) => {
  if (!DEFAULT_GRAPHS.includes(selectedGraph)) return false;

  return (
    <MenuItemWithoutPadding disableRipple>
      {_.chunk(graphs, chunks).map((graphChunk, _idx) => (
        <Flex direction='column' key={_idx}>
          {graphChunk.map((graphType, idx) => {
            return (
              <Margin margin='tiny' key={idx}>
                <LBButton
                  variant='outline'
                  size='icon'
                  onClick={() => onChange(graphType)}
                >
                  <Icon
                    data-testid={`graph-box-${idx}-${_idx}`}
                    size='medium'
                    name={GRAPH_TO_ICON[graphType]}
                    alpha={0.7}
                    {...(graphType !== selectedGraph
                      ? { color: 'botticelli' }
                      : {})}
                  />
                </LBButton>
              </Margin>
            );
          })}
        </Flex>
      ))}
    </MenuItemWithoutPadding>
  );
};

export default GraphPicker;
